
/* global dataGridRef */

import { put, select } from 'redux-saga/effects'

import request from '../../store/sagas/utils'
import {
  GET_TRANSCRIPTS,
  RELOAD_SELECTED_TRANSCRIPT_FOR_PREVIEW,
  TRY_SET_SELECTED_TRANSCRIPT_FOR_PREVIEW,
  CLEAR_FILTER,
  REMOVE_SELECTED_TRANSCRIPTS,
  REMOVE_TRANSCRIPTS,
  ON_EXPORTING,
  TRY_DESELECT_TRANSCRIPT,
  TRY_UPDATE_SELECTED_TRANSCRIPTS,
  GET_TRANSCRIPTS_FOR_EXPORT,
  TRY_SEND_TO_EMAIL,
  SEND_TO_EMAIL,
  TRY_STOP_SELECTION_WITH_CHECKBOXES,
  DOWNLOAD_FILE,
  FILE_FOR_PREVIEW_START_DOWNLOAD,
  setTotalCount,
  setSelectedTranscriptForPreview,
  trySetSelectedTranscriptForPreview,
  onRemovedTranscripts,
  getTranscripts,
  getTranscriptsForExport,
  selectTranscripts,
  removeTranscripts,
  sendToEmail,
  fileForPreviewWasDownload,
} from './transcriptViewerActions'
import {
  showError, showAlert, hideFullScreenSpinner, showLoadingBar, setAccountTimeZone,
} from '../../store/actions/appActions'
import { loginError } from '../login/loginActions'
import {
  countNumberTranscriptsByFilters,
  saveXLSX,
  saveTXT,
  collectFilters,
  getVisibleColumnsDataField,
  transformLoadOptionsToQueryString,
  tryAddStateClassesToDataGrid,
  merge2Filters,
} from './utils'
import { deepClone } from '../../libs/utils'
import { store } from '../../store/configureStore'
import { middlewaresForRequest, middlewaresForResponse } from './middlewares-for-request'

const TRANSCRIPT_API = 'https://transcript-api.providesupport.com/v1'
export const TRANSCRIPT_API_TRANSCRIPTS = `${TRANSCRIPT_API}/transcripts`
const TRANSCRIPT_API_TRANSCRIPTS_EXPORT = `${TRANSCRIPT_API}/transcripts-export`

const NUMBER_TRANSCRIPTS_FOR_NOT_SHOW_POPUP = 1
const NUMBER_TRANSCRIPTS_FOR_NOT_SHOW_POPUP_XLSX = 10000
const MAXIMUM_NUMBER_REQUESTED_TRANSCRIPTS = 100
const MAXIMUM_NUMBER_REQUESTED_TRANSCRIPTS_WITHOUT_MESSAGES = 10000

const MAXIMUM_NUMBER_OF_CONCURRENT_REQUESTS = 75
let numberOfRequestsSentPendingResponse = 0
let requestQueueToSend = []

export default [
  [GET_TRANSCRIPTS, function* ({ payload }) {
    if (numberOfRequestsSentPendingResponse >= MAXIMUM_NUMBER_OF_CONCURRENT_REQUESTS) {
      if (payload.type === 'export') {
        requestQueueToSend.push(payload)
      } else {
        requestQueueToSend.unshift(payload)
      }
      return
    }

    let { loadOptions, history, isGetTranscriptsWithoutMessages, resolve, reject } = payload
    loadOptions = deepClone(loadOptions)
    const {
      app: { account, texts },
      transcriptViewer: { preview, totalCount },
    } = yield select(state => state);

    console.log('request before middlewares', JSON.parse(JSON.stringify(loadOptions)))

    for (let ii = 0, isBreak = false; ii < middlewaresForRequest.length; ii++) {
      loadOptions = middlewaresForRequest[ii]({
        loadOptions,
        totalCount,
        preview,
        account,
        onResolve: resolveParams => {
          resolve(resolveParams)
          isBreak = true
        },
      })
      if (isBreak) return
    }

    console.log('request after middlewares', JSON.parse(JSON.stringify(loadOptions)))

    const queryString = transformLoadOptionsToQueryString(loadOptions)
    numberOfRequestsSentPendingResponse++
    yield request({
      url: `${isGetTranscriptsWithoutMessages
        ? TRANSCRIPT_API_TRANSCRIPTS_EXPORT : TRANSCRIPT_API_TRANSCRIPTS}${queryString}`,
      headers: {
        authorization: `Basic ${window.btoa(`${account.accountName}:${account.passwordMD5}`)}`,
      },
      * onSuccess(data) {
        let { transcripts, accountInfo, summary, groupCount } = data
        if (data.totalCount) {
          yield put(setTotalCount(data.totalCount.toString()))
        }

        console.log('response before middlewares', JSON.parse(JSON.stringify(transcripts)))

        if (transcripts && transcripts[0]) {
          for (let ii = 0; ii < middlewaresForResponse.length; ii++) {
            transcripts = middlewaresForResponse[ii]({
              transcripts, loadOptions, timeZone: accountInfo.timeZone,
            })
          }
        }

        console.log('response after middlewares', JSON.parse(JSON.stringify(transcripts)))

        resolve({
          data: transcripts,
          totalCount: data.totalCount,
          summary,
          groupCount,
        });
        yield onFinally()

        if (account.timeZone !== accountInfo.timeZone) {
          yield put(setAccountTimeZone({ timeZone: accountInfo.timeZone }))
        }
      },
      * onError(err) {
        console.error(err);
        if (err.errorCode === 'Unauthorized' || err.httpStatusCode === 401) { /* login error */
          yield put(loginError({ history, response: err }))
          reject();
          return
        }
        resolve({
          data: [],
          totalCount: 0,
          summary: 0,
          groupCount: 0,
        });
        yield put(showError(texts['data-loading-error']))
        yield onFinally()
      },
    });

    tryAddStateClassesToDataGrid({});

    /* local functions */
    function* onFinally() {
      numberOfRequestsSentPendingResponse--
      yield put(hideFullScreenSpinner())
      if (requestQueueToSend[0]) {
        yield put(getTranscripts(requestQueueToSend.shift()))
      }
    }
  }],


  [RELOAD_SELECTED_TRANSCRIPT_FOR_PREVIEW, function* () {
    const { app: { texts }, transcriptViewer: { preview } } = yield select(state => state)
    if (!preview.transcript?.transcriptId) return

    let responseResolve
    const responsePromise = new Promise(resolve => { responseResolve = resolve })
    yield put(getTranscripts({
      loadOptions: {
        filter: ['transcriptId', '=', preview.transcript.transcriptId],
        requireTotalCount: false,
        skip: 0,
        take: 1,
      },
      type: 'single',
      resolve: responseResolve,
      reject: () => responseResolve(null),
    }))
    const response = yield responsePromise

    if (!response?.data?.length) {
      yield put(showError(texts['data-loading-error']))
      return
    }

    yield put(trySetSelectedTranscriptForPreview(response.data[0]))
  }],

  [TRY_SET_SELECTED_TRANSCRIPT_FOR_PREVIEW, function* ({ payload: newSelectedTranscriptForPreview }) {
    const {
      transcriptViewer: { preview: { transcript: oldSelectedTranscriptForPreview, media: { linksFromBlobByURL } } },
    } = yield select(state => state)
    if (oldSelectedTranscriptForPreview
      && newSelectedTranscriptForPreview.transcriptId === oldSelectedTranscriptForPreview.transcriptId) return

    /* remove linksFromBlobByURL from RAM */
    Object.values(linksFromBlobByURL).forEach(blobUrl => URL.revokeObjectURL(blobUrl))

    yield put(setSelectedTranscriptForPreview(newSelectedTranscriptForPreview))
  }],

  [CLEAR_FILTER, () => {
    dataGridRef.current.instance.clearFilter()
    dataGridRef.current.instance.clearSorting()
    dataGridRef.current.instance.clearGrouping()
    // dataGridRef.current.instance.clearSelection()
  }],

  [REMOVE_SELECTED_TRANSCRIPTS, function* () {
    const { app: { texts }, transcriptViewer: { totalCount } } = yield select(state => state);
    const { selectionFilter, filter } = collectFilters({ dataGridRef, isAll: false })
    const numberSelectedTranscripts = countNumberTranscriptsByFilters(selectionFilter, totalCount)
    if (!numberSelectedTranscripts) return
    if (numberSelectedTranscripts === 1) {
      yield put(removeTranscripts(filter))
      return
    }
    yield put(showAlert({
      message: texts['confirm-delete-message_several'].replace('[NUMBER]', numberSelectedTranscripts),
      buttons: [
        {
          label: texts['yes'],
          action: { actionType: 'transcriptViewerActions', actionName: 'removeTranscripts', actionParams: filter },
        },
        {
          label: texts['no'],
          onClick: () => {},
        },
      ],
      className: 'confirm-delete',
    }))
  }],

  [REMOVE_TRANSCRIPTS, function* ({ payload: filter }) {
    const { app: { account, texts }, transcriptViewer: { lastClickTimeSelectAll } } = yield select(state => state);
    dataGridRef.current.instance.beginCustomLoading()
    if (
      filter !== undefined
      && (filter === null || filter[0] === '!' || (filter[0] && filter[0][0] === '!'))
      && lastClickTimeSelectAll
    ) {
      filter = merge2Filters(filter, ['startDate', '<', lastClickTimeSelectAll])
    }

    yield request({
      url:
        `${TRANSCRIPT_API_TRANSCRIPTS}${transformLoadOptionsToQueryString({ filter })}&method=delete&criteria=filter`,
      headers: {
        authorization: `Basic ${window.btoa(`${account.accountName}:${account.passwordMD5}`)}`,
      },
      method: 'POST',
      * onSuccess(data) {
        dataGridRef.current.instance.endCustomLoading()

        dataGridRef.current.instance.refresh()
        yield put(onRemovedTranscripts())
      },
      * onError(err) {
        dataGridRef.current.instance.endCustomLoading()
        yield put(showError(texts['data-loading-error']))
      },
    })
  }],

  [ON_EXPORTING, function* ({ payload: { dataGridRef, typeExportedFiles, isExportAll } }) {
    const {
      app: { texts },
      transcriptViewer: { totalCount },
    } = yield select(state => state)
    const { selectionFilter, filter } = collectFilters({ dataGridRef, isAll: isExportAll })
    let exportParams = {
      typeExportedFiles,
      selectionFilter,
      filter,
      isExportAll,
      numberExportedTranscripts: countNumberTranscriptsByFilters(selectionFilter, totalCount),
      visibleColumnsDataField: getVisibleColumnsDataField(dataGridRef),
      numberOfTranscriptsInFile: typeExportedFiles === 'xlsx' ? 10000 : 1,
      maximumNumberRequestedTranscripts: typeExportedFiles === 'xlsx'
        ? MAXIMUM_NUMBER_REQUESTED_TRANSCRIPTS_WITHOUT_MESSAGES
        : MAXIMUM_NUMBER_REQUESTED_TRANSCRIPTS,
      numberTranscriptsForNotShowPopup: typeExportedFiles === 'xlsx'
        ? NUMBER_TRANSCRIPTS_FOR_NOT_SHOW_POPUP_XLSX
        : NUMBER_TRANSCRIPTS_FOR_NOT_SHOW_POPUP,
      format: typeExportedFiles === 'xlsx' ? 'xlsx'
        : typeExportedFiles === 'txt' ? 'text/plain'
          : typeExportedFiles === 'html' ? 'text/html'
            : 'text/plain',
      fileTypeFullName: texts[`file-type-full-name_${typeExportedFiles}`],
      dataGridRef,
    }
    exportParams.numberOfFiles =
      Math.ceil(exportParams.numberExportedTranscripts / exportParams.maximumNumberRequestedTranscripts)
    const {
      numberExportedTranscripts,
      maximumNumberRequestedTranscripts,
      numberOfFiles,
      fileTypeFullName,
      numberTranscriptsForNotShowPopup,
    } = exportParams

    console.warn('numberExportedTranscripts', numberExportedTranscripts)
    console.warn('selectionFilter', selectionFilter, JSON.stringify(selectionFilter))
    console.warn('dataGridRef', dataGridRef)

    /* no popup */
    if (numberExportedTranscripts <= numberTranscriptsForNotShowPopup) {
      yield put(getTranscriptsForExport(exportParams))
      return
    }

    /* popup ******************************************************************************************************* */
    const actualizeText = text => text
      .replace(/\[numberExportedTranscripts\]/g, numberExportedTranscripts)
      .replace(/\[numberOfFiles\]/g, numberOfFiles)
      .replace(/\[numberIn1File\]/g, maximumNumberRequestedTranscripts)
      .replace(/\[fileTypeFullName\]/g, fileTypeFullName)

    /* message */
    let message = ''
    if (typeExportedFiles === 'xlsx') {
      message += actualizeText(texts['export-popup_description1_if-more-1-xlsx'])
    } else {
      if (numberOfFiles > 1) {
        message += actualizeText(texts['export-popup_description1_if-more-max'])
      } else {
        message += actualizeText(texts['export-popup_description1_if-more-1-but-less-max'])
      }
      message += `</br></br>${actualizeText(texts['export-popup_description2'])}`
    }

    /* buttons */
    let buttons = []
    if (typeExportedFiles !== 'xlsx') {
      buttons.push({
        label: texts['export-each-to-a-separate-file'],
        action: { actionType: 'transcriptViewerActions', actionName: 'getTranscriptsForExport', actionParams: exportParams },
      })
    }
    if (numberOfFiles > 1) {
      buttons.push({
        label: actualizeText(texts[
          typeExportedFiles === 'xlsx' ? 'export-to-number-files-of-number-xlsx' : 'export-to-number-files-of-number'
        ]),
        action: { actionType: 'transcriptViewerActions', actionName: 'getTranscriptsForExport', actionParams: { ...exportParams, numberOfTranscriptsInFile: maximumNumberRequestedTranscripts } },
      })
    }
    buttons.push({
      label: texts['export-everything-to-1-file'],
      action: { actionType: 'transcriptViewerActions', actionName: 'getTranscriptsForExport', actionParams: { ...exportParams, numberOfTranscriptsInFile: numberExportedTranscripts } },
    })
    buttons.push({ label: texts['cancel'], onClick: () => {} })

    yield put(showAlert({
      title: texts[typeExportedFiles === 'xlsx' ? 'export-popup_title-xlsx' : 'export-popup_title'],
      message,
      buttons,
    }))
  }],

  [GET_TRANSCRIPTS_FOR_EXPORT, function* ({ payload: {
    numberExportedTranscripts,
    filter,
    numberOfTranscriptsInFile,
    maximumNumberRequestedTranscripts,
    typeExportedFiles,
    format,
    visibleColumnsDataField,
    dataGridRef,
  } }) {
    const {
      app: { account: { accountName }, texts },
    } = yield select(state => state)
    let transcriptList = []
    let responsesAwaitingProcessing = {}
    let numberOfReceived = 0
    let numberOfSaved = 0
    // const numberOfExpectedResponses = Math.ceil(numberExportedTranscripts / maximumNumberRequestedTranscripts)
    /* попытаться использовать закешированные данные */
    const transcriptsFromCache = undefined
    if (transcriptsFromCache) {
      // https://supportcenter.devexpress.com/ticket/details/t1132559/how-to-get-cached-rows
      transcriptList = transcriptsFromCache
    } else { /* получить с транскрипты с сервера */
      yield put(showLoadingBar({ total: numberExportedTranscripts, first: 0, second: 0 }))

      for (
        let index = 0, skip = 0;
        skip < numberExportedTranscripts;
        index++, skip += maximumNumberRequestedTranscripts
      ) {
        yield put(getTranscripts({
          loadOptions: {
            skip,
            take: maximumNumberRequestedTranscripts,
            filter,
          },
          history: null,
          type: 'export',
          isGetTranscriptsWithoutMessages: typeExportedFiles === 'xlsx',
          resolve: saveResponseForProcessing(index),
          reject: onError,
        }))
      }
    }
    function onError() {
    }

    /* обработать данные и сохранить в файл(ы) */
    let responseProcessingIndex = 0
    function saveResponseForProcessing(index) {
      return response => {
        responsesAwaitingProcessing[index] = response.data
        numberOfReceived += response.data.length
        store.dispatch(
          showLoadingBar({ total: numberExportedTranscripts, first: numberOfReceived, second: numberOfSaved }),
        )
        addToTranscriptList()
      }
    }
    function addToTranscriptList() {
      if (!responsesAwaitingProcessing[responseProcessingIndex]) return
      transcriptList = transcriptList.concat(responsesAwaitingProcessing[responseProcessingIndex])
      delete responsesAwaitingProcessing[responseProcessingIndex]
      const isLastResponse =
        responseProcessingIndex >= Math.floor(numberExportedTranscripts / maximumNumberRequestedTranscripts)
      responseProcessingIndex++
      tryCollectAndSaveFile(isLastResponse)
    }
    function tryCollectAndSaveFile(isLastResponse) {
      if (transcriptList.length >= numberOfTranscriptsInFile || isLastResponse) {
        collectAndSaveFile(transcriptList.slice(0, numberOfTranscriptsInFile))
        transcriptList = transcriptList.slice(numberOfTranscriptsInFile)
        if (transcriptList.length) tryCollectAndSaveFile(isLastResponse)
        else if (!isLastResponse) addToTranscriptList()
      } else {
        addToTranscriptList()
      }
    }
    function collectAndSaveFile(transcripts) {
      let fileName = 'PS-Trancripts'
      if (numberOfTranscriptsInFile === 1) {
        if (transcripts[0].startDate) {
          fileName += ` ${new Date(transcripts[0].startDate).toLocaleString()}`
        }
      } else {
        fileName += ` ${numberOfSaved + 1}-${numberOfSaved + transcripts.length}`
      }
      fileName += `.${typeExportedFiles}`

      if (typeExportedFiles === 'xlsx') {
        saveXLSX({ transcripts, fileName, visibleColumnsDataField, texts })
      } else {
        saveTXT({ transcripts, format, fileName, texts, accountName })
      }

      numberOfSaved += transcripts.length
      store.dispatch(
        showLoadingBar({
          total: numberExportedTranscripts,
          first: numberOfReceived,
          second: numberOfSaved,
          onClose: () => {
            tryAddStateClassesToDataGrid({})
          },
        }),
      )
    }
  }],

  [TRY_DESELECT_TRANSCRIPT, function* ({ payload: { loadOptions, onDeselectionFromTable }, dispatch }) {
    const { transcriptViewer: { selectedTranscripts } } = yield select(state => state);
    const isTranscriptNotSelected = !selectedTranscripts[0].transcriptId
    if (isTranscriptNotSelected) return

    const { filter } = loadOptions
    if (!filter) return

    onDeselectionFromTable()
  }],

  [TRY_UPDATE_SELECTED_TRANSCRIPTS, function* () {
    const {
      transcriptViewer: {
        preview: { transcript: oldSelectedTranscriptForPreview },
        selectionFilter: oldSelectionFilter,
        lastClickTimeSelectAll: oldLastClickTimeSelectAll,
        totalCount,
      },
    } = yield select(state => state);

    tryAddStateClassesToDataGrid({ totalCount })

    let selectionFilter = dataGridRef.current.instance.state().selectionFilter
    if (selectionFilter === undefined) return
    if (
      oldSelectedTranscriptForPreview
      && JSON.stringify(oldSelectionFilter) === JSON.stringify(selectionFilter)
    ) return

    let selectedTranscriptForPreview
    let lastClickTimeSelectAll = oldLastClickTimeSelectAll
    if (selectionFilter === null) { /* если все выбраны */
      /* показать первый видимый */
      let rows = dataGridRef.current.instance.getVisibleRows()
      for (let ii = 0; ii < rows.length; ii++) {
        if (rows[ii].rowType === 'group') continue
        selectedTranscriptForPreview = rows[ii].data
        break
      }
      if (oldSelectionFilter !== null) lastClickTimeSelectAll = +new Date()
    } else if (
      selectionFilter[0] === '!' /* если выбраны все кроме */
      || (selectionFilter[0] && selectionFilter[0][0] === '!') /* если выбраны все кроме нескольких */
    ) {
      /* показать первый подходящий из видимых */
      let exceptions = {}
      if (selectionFilter[0] === '!') {
        exceptions[selectionFilter[1][2]] = 1
      } else {
        for (let ii = 0; ii < selectionFilter.length; ii += 2) {
          exceptions[selectionFilter[ii][1][2]] = 1
        }
      }
      let rows = dataGridRef.current.instance.getVisibleRows()
      for (let ii = 0; ii < rows.length; ii++) {
        if (rows[ii].rowType === 'group') continue
        if (!exceptions[rows[ii].data.transcriptId]) {
          selectedTranscriptForPreview = rows[ii].data
          break
        }
      }
    } else if (Array.isArray(selectionFilter) && selectionFilter.length === 0) { /* ничего не выбрано */
      lastClickTimeSelectAll = undefined
    } else {
      /* если выбраны несколько */
      let lastFilter = selectionFilter
      if (Array.isArray(selectionFilter[0])) {
        lastFilter = selectionFilter[selectionFilter.length - 1]
      }
      let transcriptId = lastFilter[2]
      const index = dataGridRef.current.instance.getRowIndexByKey(transcriptId)
      if (index !== -1) selectedTranscriptForPreview = dataGridRef.current.instance.getVisibleRows()[index].data
    }

    yield put(selectTranscripts({
      selectionFilter,
      lastClickTimeSelectAll,
    }))
    if (selectedTranscriptForPreview) {
      yield put(trySetSelectedTranscriptForPreview(selectedTranscriptForPreview))
    }
  }],

  [TRY_STOP_SELECTION_WITH_CHECKBOXES, () => {
    const selectionFilter = dataGridRef.current.instance.state().selectionFilter
    const is1TranscriptSelected = selectionFilter && selectionFilter[0] && !Array.isArray(selectionFilter[0])
    if (is1TranscriptSelected) {
      dataGridRef.current.instance.stopSelectionWithCheckboxes();
    }
  }],

  [TRY_SEND_TO_EMAIL, function* ({ payload: transcriptId }) {
    const { app: { texts } } = yield select(state => state);

    window.sendTranscriptToEmail = (form, id) => {
      store.dispatch(sendToEmail({ email: form[0].value, transcriptId: id }))
      window.closeCurrentAlert()
    }

    yield put(showAlert({
      title: texts['send-to-email_title'],
      message: `<form onsubmit="sendTranscriptToEmail(this, '${transcriptId}'); return false">
        <input type="email" placeholder="${texts['send-to-email_placeholder']}" minlength="3" maxlength="320" required>
        <br>
        <button type="submit">${texts['send-to-email_send']}</button>
      </form>`,
      className: 'confirm-sendToEmail',
    }))
  }],

  [SEND_TO_EMAIL, function* ({ payload: { email, transcriptId } }) {
    const { app: { account } } = yield select(state => state);

    yield request({
      url: `${TRANSCRIPT_API_TRANSCRIPTS}/${transcriptId}/send-email?recipientEmail=${encodeURIComponent(email)}`,
      method: 'POST',
      headers: {
        authorization: `Basic ${window.btoa(`${account.accountName}:${account.passwordMD5}`)}`,
      },
    })
  }],

  [DOWNLOAD_FILE, function* ({ payload: { url, onSuccess } }) {
    const { app: { texts, account } } = yield select(state => state);
    yield request({
      url,
      method: 'GET',
      responseType: 'blob',
      cache: 'force-cache',
      headers: {
        authorization: `Basic ${window.btoa(`${account.accountName}:${account.passwordMD5}`)}`,
      },
      * onSuccess(res) { yield onSuccess(res) },
      * onError(err) {
        yield put(showError(`${texts['data-loading-error']} ${err.toString()}`))
      },
    })
  }],
  [FILE_FOR_PREVIEW_START_DOWNLOAD, function* ({ payload: url }) {
    const { app: { texts, account } } = yield select(state => state);
    yield request({
      url,
      method: 'GET',
      responseType: 'blob',
      cache: 'force-cache',
      headers: {
        authorization: `Basic ${window.btoa(`${account.accountName}:${account.passwordMD5}`)}`,
      },
      * onSuccess(blob) {
        yield put(fileForPreviewWasDownload({ [url]: URL.createObjectURL(blob) }))
      },
      * onError(err) {
        yield put(showError(`${texts['data-loading-error']} ${err.toString()}`))
      },
    })
  }],
]
